import {
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogText,
  DialogTitle,
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { LoadingState } from '@src/components/common/Spinner';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import SpeakerDraftForm from '@src/components/sidepanel/modals/SpeakerDraftForm';
import SpeakerMomentCell from '@src/components/sidepanel/modals/SpeakerMomentCell';
import { useOverlayDismiss } from '@src/hooks/useOverlayDismiss';
import { useSpeakerFrameThumbnails } from '@src/hooks/useSpeakerFrameThumbnails';
import { useSpeakerIdentificationService } from '@src/hooks/useSpeakerIdentificationService';
import {
  type AnonymousSpeaker,
  getSpeakerColor,
  type SpeakerWithColor,
} from '@src/services/SpeakerManagementService';
import {
  cacheAndWriteSpeakerAssignment,
  notifySpeakerAddedToStory,
  notifySpeakerCacheUpdated,
} from '@src/stores-v2/speakerCache';
import { useDatoClient, useDatoPerson } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import PencilIcon from '@src/svgs/PencilIcon';
import PlusIcon from '@src/svgs/PlusIcon';
import {
  getRepresentativeTurnBySpeaker,
  getThumbnailFrameNumbersBySpeaker,
} from '@src/utility/frameExtraction';
import { LogManager } from '@src/utility/Logger';
import {
  findBestSpeakerMatch,
  MATCH_THRESHOLD_AUTO,
  MATCH_THRESHOLD_REVIEW,
  rankSpeakerMatches,
} from '@src/utility/speakerFuzzyMatch';
import { isRoleDescription } from '@src/utility/speakerIdentification';
import {
  clearSpeakerCache as clearSpeakerUtilsCache,
  getSpeakerColorFromLabel,
  updateSpeakerVoiceMappings,
} from '@src/utility/speakerUtils';
import { resolveStoryPlaybackSource } from '@src/utility/storyPlaybackSource';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type { IdentificationProgress } from '@src/services/speakers/SpeakerIdentificationService';
import type { FrameAnalysisResult } from '@src/utility/frameExtraction';
import type { IdentifiedSpeaker } from '@src/utility/speakerIdentification';

const logger = LogManager.getLogger(
  'component:CreatorStudioSpeakerIdentificationModal',
);

// ============================================================================
// TYPES
// ============================================================================

interface ExistingSpeaker extends SpeakerWithColor {
  datoSpeakerId: string;
}

interface CreatorStudioSpeakerIdentificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  anonymousSpeakers: AnonymousSpeaker[];
  currentMappings?: Record<string, string> | null;
  onIdentificationComplete: (mappedLabels?: string[]) => void;
  /**
   * An identification already run elsewhere (the post-upload background run):
   * the modal opens straight onto it, as if "Analyze Transcript" had just
   * finished.
   */
  precomputed?: {
    identified: IdentifiedSpeaker[];
    frameAnalysisResults: FrameAnalysisResult[];
  };
  /**
   * Run "Analyze Transcript" on open instead of waiting for the click. With
   * `precomputed` or `autoIdentify` the modal stays hidden until its result is
   * on screen; `onShown` fires as it appears.
   */
  autoIdentify?: boolean;
  onShown?: () => void;
}

const TITLE_ID = 'speaker-identification-title';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CreatorStudioSpeakerIdentificationModal: React.FC<
  CreatorStudioSpeakerIdentificationModalProps
> = ({
  isOpen,
  onClose,
  anonymousSpeakers,
  currentMappings,
  onIdentificationComplete,
  precomputed,
  autoIdentify = false,
  onShown,
}) => {
  const [isIdentifying, setIsIdentifying] = useState(false);
  // Identification has run: the rows are on screen, even when it named nobody
  // — a speaker the transcript could not place can still be named by hand.
  const [hasResult, setHasResult] = useState(false);
  const [resultSummary, setResultSummary] = useState('');
  const [identifiedSpeakers, setIdentifiedSpeakers] = useState<
    IdentifiedSpeaker[]
  >([]);
  // Names the user typed for a row. They stand in for the transcript's
  // candidate in that row's menu, so a typed name is offered as "New" exactly
  // the way an identified one is — and can be picked over an existing match.
  const [typedNames, setTypedNames] = useState<
    Record<string, IdentifiedSpeaker>
  >({});
  const [selectedMappings, setSelectedMappings] = useState<
    Record<string, IdentifiedSpeaker | ExistingSpeaker | null>
  >({});
  // Labels the user picked for themselves, as opposed to accepting the
  // auto-mapped default. Only handleMappingChange writes here.
  const [explicitlyChosen, setExplicitlyChosen] = useState<Set<string>>(
    new Set(),
  );
  const [error, setError] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applyingProgress, setApplyingProgress] = useState<string>('');
  const [existingSpeakers, setExistingSpeakers] = useState<ExistingSpeaker[]>(
    [],
  );
  // Auto-identify waits for this: its auto-mapping matches against them.
  const [existingSpeakersLoaded, setExistingSpeakersLoaded] = useState(false);
  const [readyToShow, setReadyToShow] = useState(false);
  const [isAnalyzingFrames, setIsAnalyzingFrames] = useState(false);
  /** Which row holds the player, and whether it is running. One at a time. */
  const [playingSpeakerLabel, setPlayingSpeakerLabel] = useState<string | null>(
    null,
  );
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [identificationProgress, setIdentificationProgress] =
    useState<string>('');

  // Creator Studio hooks
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const datoPerson = useDatoPerson();
  // The story's transcript, deliberately not `useTranscription()`. Everything
  // this modal does is story-scoped — frames are requested for the story video,
  // mappings are written per story, and speakers are attached to the story — so
  // a clip-windowed view would sample the wrong span. It also keeps the modal
  // on the same transcript ChatGPTService reads (`videoCreator.rootStore`),
  // rather than reasoning about one transcript while the model reads another.
  const transcriptionStore = videoCreator.rootStore?.transcriptionStore;

  const storyId = videoCreator.story?.id;
  const albumId = videoCreator.story?.primaryShowcase?.id;

  // Where each speaker's first word lands, in story seconds, and the frame that
  // shows them. Derived rather than held: both are a function of the transcript
  // the modal already reads. Driven off the raw elements rather than `segments`,
  // which needs an `end_ts` on every element and comes back empty for
  // transcripts that lack them.
  //
  // `getOriginalTranscription()` returns a stored reference, so `elements` is a
  // stable dep — and `thumbnailFrameNumbers` must stay stable, since
  // `useSpeakerFrameThumbnails` lists it in a `useEffect` (pitfall 18).
  const originalElements =
    transcriptionStore?.getOriginalTranscription()?.elements;

  // Both the moment to play from and the frame to show come off one walk. Not
  // the speaker's first words: diarization opens speakers with sub-second blips
  // — a word misattributed inside someone else's sentence — and the row would
  // show and play that. See `getRepresentativeTurnBySpeaker`.
  const representativeTurns = useMemo(
    () => getRepresentativeTurnBySpeaker(originalElements || []),
    [originalElements],
  );

  const thumbnailFrameNumbers = useMemo(() => {
    const framesBySpeaker =
      getThumbnailFrameNumbersBySpeaker(representativeTurns);
    // Only the labels this modal renders a row for. A story's transcript also
    // carries speakers mapped long ago, and a frame fetched for a row that does
    // not exist is a request for nothing.
    const rows = new Set(anonymousSpeakers.map(s => s.speakerLabel));
    return Object.fromEntries(
      Object.entries(framesBySpeaker).filter(([label]) => rows.has(label)),
    );
  }, [representativeTurns, anonymousSpeakers]);

  // Diagnostic only: previews simply do not appear, so say why once the
  // transcript is in rather than leaving a silent absence.
  useEffect(() => {
    if (
      originalElements?.length &&
      Object.keys(representativeTurns).length === 0
    ) {
      logger.warn(
        'No transcript elements carry both a speaker_label and a numeric ts — no speaker previews can be resolved',
      );
    }
  }, [originalElements, representativeTurns]);

  const speakerThumbnails = useSpeakerFrameThumbnails(
    storyId,
    thumbnailFrameNumbers,
  );
  const playbackSource = resolveStoryPlaybackSource(
    videoCreator.story,
    'lowest-first',
  );

  const togglePreview = (speakerLabel: string) => {
    if (speakerLabel !== playingSpeakerLabel) {
      setPlayingSpeakerLabel(speakerLabel);
      setIsPreviewPlaying(true);
      return;
    }
    setIsPreviewPlaying(playing => !playing);
  };

  /** The player ran out of story; the row goes back to being a still. */
  const stopPreview = () => {
    setPlayingSpeakerLabel(null);
    setIsPreviewPlaying(false);
  };

  const identificationService = useSpeakerIdentificationService();
  const { handleOverlayMouseDown, handleOverlayClick } =
    useOverlayDismiss(onClose);

  // Track whether we've already run identification so we don't
  // reset mappings when the parent refreshes anonymousSpeakers
  const hasIdentifiedRef = useRef(false);
  const autoIdentifyStartedRef = useRef(false);

  // Initialize selected mappings and load existing speakers when modal opens
  useEffect(() => {
    if (!isOpen) {
      // Reset on close so next open starts fresh
      hasIdentifiedRef.current = false;
      autoIdentifyStartedRef.current = false;
      setExistingSpeakersLoaded(false);
      setReadyToShow(false);
      setExplicitlyChosen(new Set());
      setTypedNames({});
      setPlayingSpeakerLabel(null);
      setIsPreviewPlaying(false);
      return;
    }

    // Don't reset mappings if identification already ran
    if (hasIdentifiedRef.current) return;

    const initialMappings: Record<
      string,
      IdentifiedSpeaker | ExistingSpeaker | null
    > = {};
    anonymousSpeakers.forEach(speaker => {
      initialMappings[speaker.speakerLabel] = null;
    });
    setSelectedMappings(initialMappings);
    // `explicitlyChosen` only annotates `selectedMappings`, so it is cleared
    // wherever the mappings are replaced wholesale. Provenance outliving the
    // choice it describes would skip the apply-time dedupe guard for a label
    // the user never actually picked.
    setExplicitlyChosen(new Set());

    // Load existing speakers from repository
    const loadExistingSpeakers = async () => {
      if (!storyId || !datoClientStore?.speakerRepository) {
        setExistingSpeakersLoaded(true);
        return;
      }
      try {
        const { storySpeakers, albumSpeakers } =
          await datoClientStore.speakerRepository.getStorySpeakers(storyId);
        // A speaker applied here is referenced by both the story and the
        // album, so the two lists overlap. Story entries come first and win
        // the dedupe: same record either way, but already being on this story
        // is the better default when scores tie.
        const seen = new Set<string>();
        const allExisting: ExistingSpeaker[] = [
          ...storySpeakers,
          ...albumSpeakers,
        ]
          .filter(s => {
            if (seen.has(s.id)) return false;
            seen.add(s.id);
            return true;
          })
          .map((s, i) => ({
            ...s,
            datoSpeakerId: s.id,
            // Hashed from the Dato id so the colour matches this speaker
            // everywhere else, rather than shifting with list position.
            color: getSpeakerColor(s.id, i),
          }));
        setExistingSpeakers(allExisting);
      } catch (err) {
        logger.error('Error loading existing speakers:', err);
      } finally {
        setExistingSpeakersLoaded(true);
      }
    };
    loadExistingSpeakers();
  }, [isOpen, anonymousSpeakers, storyId, datoClientStore?.speakerRepository]);

  // Handle escape key — not while auto-identify runs out of sight, where an
  // Escape meant for the page would silently cancel it. On `window`, not
  // `document`: a row's open menu (useAnchoredDropdown) stops Escape on
  // `document`, so the keypress closes the menu and leaves the modal open.
  const isHiddenWhileIdentifying =
    (autoIdentify || !!precomputed) && !readyToShow;
  useEffect(() => {
    if (!isOpen || isHiddenWhileIdentifying) return;

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, isHiddenWhileIdentifying, onClose]);

  useEffect(() => {
    if (!isOpen || autoIdentifyStartedRef.current) return;
    if (!precomputed && !autoIdentify) return;
    // Auto-mapping matches against the existing speakers, so wait for them.
    if (!existingSpeakersLoaded) return;
    autoIdentifyStartedRef.current = true;
    const show = () => {
      setReadyToShow(true);
      onShown?.();
    };
    if (precomputed) {
      applyIdentification(precomputed.identified);
      show();
      return;
    }
    // Resolves (never rejects) once every pass is done, success or error, so
    // the modal always appears — on the suggestions, or on the error to retry.
    void handleIdentifySpeakers().finally(show);
    // Once per open; the handlers are re-created every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, precomputed, autoIdentify, existingSpeakersLoaded]);

  if (!isOpen) return null;

  // ============================================================================
  // IDENTIFICATION (4-PASS)
  // ============================================================================

  /** Show an identification result: summary, then auto-map each speaker. */
  const applyIdentification = (result: IdentifiedSpeaker[]) => {
    // A role the frames could not turn into a name ("Vanguard executive")
    // named no one: offering it as New would create a Dato speaker by that
    // label, so the row starts as "Not identified" instead.
    const identified = result.filter(id => !isRoleDescription(id));
    const count = identified.length;
    setResultSummary(
      count === 0
        ? 'No speaker introduced themselves or was mentioned by name. Name them below, or skip for now.'
        : `Found ${count} speaker${count !== 1 ? 's' : ''} in the transcript. Confirm each match, or pick who it should be.`,
    );
    setIdentifiedSpeakers(identified);
    setHasResult(true);
    hasIdentifiedRef.current = true;

    // Auto-map identified speakers with match checking
    const newMappings: Record<
      string,
      IdentifiedSpeaker | ExistingSpeaker | null
    > = {};
    anonymousSpeakers.forEach(speaker => {
      const identifiedMatch = identified.find(
        id => id.speakerLabel === speaker.speakerLabel,
      );

      if (identifiedMatch) {
        // Only a high-confidence match may be chosen for the user; anything
        // weaker stays "New" and the user picks from the dropdown. Same call
        // handleApplyMappings makes, so both stages agree on what a match is.
        const bestMatch = findBestSpeakerMatch(
          identifiedMatch,
          existingSpeakers,
          MATCH_THRESHOLD_AUTO,
        );
        newMappings[speaker.speakerLabel] = bestMatch?.match ?? identifiedMatch;
      } else {
        newMappings[speaker.speakerLabel] = null;
      }
    });
    setSelectedMappings(newMappings);
  };

  const handleIdentifySpeakers = async () => {
    if (!identificationService || !storyId) {
      setError('ChatGPT service not available');
      return;
    }

    setIsIdentifying(true);
    setError(null);

    try {
      // The original story transcript (not the clip transcript), so
      // identification covers every speaker across the full story.
      const input = {
        storyId,
        elements:
          transcriptionStore?.getOriginalTranscription()?.elements || [],
        anonymousSpeakers,
      };
      const onProgress = (progress: IdentificationProgress) => {
        if (progress.message !== undefined) {
          setIdentificationProgress(progress.message);
        }
        if (progress.analyzingFrames !== undefined) {
          setIsAnalyzingFrames(progress.analyzingFrames);
        }
      };

      const fromTranscript = await identificationService.runTranscriptPass(
        input,
        onProgress,
      );
      if (!fromTranscript.ok) throw new Error(fromTranscript.error.message);
      const result = await identificationService.runFramePasses(
        input,
        fromTranscript.value,
        onProgress,
      );
      if (!result.ok) throw new Error(result.error.message);

      applyIdentification(result.value.identified);
    } catch (err) {
      logger.error('Error identifying speakers:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(
        `Failed to identify speakers: ${errorMessage}. Please try again or contact support if the issue persists.`,
      );
    } finally {
      setIsIdentifying(false);
    }
  };

  // ============================================================================
  // MAPPINGS
  // ============================================================================

  const handleMappingChange = (
    speakerLabel: string,
    mapping: IdentifiedSpeaker | ExistingSpeaker | null,
  ) => {
    setSelectedMappings(prev => ({
      ...prev,
      [speakerLabel]: mapping,
    }));
    setExplicitlyChosen(prev => new Set(prev).add(speakerLabel));
  };

  /** The name on offer for a row: what the user typed, else the transcript's. */
  const candidateFor = (speakerLabel: string): IdentifiedSpeaker | undefined =>
    typedNames[speakerLabel] ??
    identifiedSpeakers.find(id => id.speakerLabel === speakerLabel);

  /**
   * A typed name is the user's choice, so it is never re-matched at apply
   * time — but it is matched here, once, against the library: typing a name
   * that is already on the org (a misspelling corrected, a person the
   * transcript never named) attaches to that person rather than minting a
   * duplicate. The typed name stays on offer as "New" in the row's menu for
   * the case where a namesake is what was meant.
   */
  const commitName = (speakerLabel: string, name: string, title: string) => {
    const candidate: IdentifiedSpeaker = { speakerLabel, name, title };
    setTypedNames(prev => ({ ...prev, [speakerLabel]: candidate }));
    const bestMatch = findBestSpeakerMatch(
      candidate,
      existingSpeakers,
      MATCH_THRESHOLD_AUTO,
    );
    handleMappingChange(speakerLabel, bestMatch?.match ?? candidate);
  };

  const handleApplyMappings = async () => {
    if (!storyId || !datoClientStore?.speakerRepository) return;

    setIsApplying(true);
    setError(null);
    setApplyingProgress('Preparing mappings...');

    try {
      // Group speakers by normalized name to detect consolidation
      const identifiedNames = new Map<string, string[]>();

      for (const [speakerLabel, mapping] of Object.entries(selectedMappings)) {
        if (mapping) {
          const name = mapping.name.toLowerCase().trim();
          const title = ('title' in mapping ? mapping.title || '' : '')
            .toLowerCase()
            .trim();
          const key = `${name}_${title}`;
          if (!identifiedNames.has(key)) {
            identifiedNames.set(key, []);
          }
          identifiedNames.get(key)!.push(speakerLabel);
        }
      }

      // Build the final mappings
      const allMappings: Record<string, string> = {
        ...(currentMappings || {}),
      };
      // Collect speakers added to story for optimistic panel update
      const addedSpeakers: Array<{
        speaker: SpeakerWithColor;
        mappedLabels: string[];
      }> = [];

      // Process each name group
      let speakerCount = 0;
      const totalGroups = identifiedNames.size;

      for (const [, speakerLabels] of identifiedNames) {
        speakerCount++;
        const primaryLabel = speakerLabels[0];
        const mapping = selectedMappings[primaryLabel];
        if (!mapping) continue;

        let datoSpeakerId: string;

        if ('datoSpeakerId' in mapping && mapping.datoSpeakerId) {
          // Existing speaker - use their ID
          datoSpeakerId = mapping.datoSpeakerId;
          setApplyingProgress(
            `Mapping ${mapping.name} (${speakerCount} of ${totalGroups})...`,
          );
          // PLAT-9178: Auto-add org speaker to story if not already present
          await datoClientStore.speakerRepository.addSpeakerReferenceToStory(
            datoSpeakerId,
            storyId,
          );
          addedSpeakers.push({
            speaker: { ...mapping, color: getSpeakerColor(datoSpeakerId, 0) },
            mappedLabels: speakerLabels,
          });
        } else {
          // New speaker - create in Dato
          setApplyingProgress(
            `Creating ${mapping.name} (${speakerCount} of ${totalGroups})...`,
          );

          // Dedupe guard for defaults the user left alone. It exists for the
          // case where the org library finished loading after identification
          // ran, so the auto-map scored against an empty list and left a
          // matchable speaker as "New". A choice the user made is never
          // second-guessed — picking "New" next to an identical-looking
          // existing row is how you deliberately create a namesake.
          const chosenByUser = speakerLabels.some(label =>
            explicitlyChosen.has(label),
          );
          const bestMatch = chosenByUser
            ? null
            : findBestSpeakerMatch(
                {
                  name: mapping.name,
                  title: 'title' in mapping ? mapping.title : undefined,
                },
                existingSpeakers,
                MATCH_THRESHOLD_AUTO,
              );
          const existingMatch = bestMatch?.match ?? null;

          if (existingMatch) {
            datoSpeakerId = existingMatch.id;
            // PLAT-9178: Auto-add matched org speaker to story if not already present
            await datoClientStore.speakerRepository.addSpeakerReferenceToStory(
              datoSpeakerId,
              storyId,
            );
            addedSpeakers.push({
              speaker: {
                ...existingMatch,
                color: getSpeakerColor(datoSpeakerId, 0),
              },
              mappedLabels: speakerLabels,
            });
          } else {
            const newSpeaker =
              await datoClientStore.speakerRepository.createSpeaker(
                mapping.name,
                'title' in mapping ? mapping.title : undefined,
                undefined,
                undefined,
                {
                  userId: datoPerson?.person?.id,
                  storyId,
                  albumId,
                },
              );

            if (!newSpeaker) {
              throw new Error(`Failed to create speaker ${mapping.name}`);
            }

            // Add to story and album
            await datoClientStore.speakerRepository.addSpeakerReferenceToStory(
              newSpeaker.id,
              storyId,
            );
            if (albumId) {
              await datoClientStore.speakerRepository.addSpeakerReferenceToAlbum(
                newSpeaker.id,
                albumId,
              );
            }

            datoSpeakerId = newSpeaker.id;
            // Brand new, so name + title is the whole record.
            addedSpeakers.push({
              speaker: {
                id: datoSpeakerId,
                name: mapping.name,
                title: 'title' in mapping ? mapping.title : undefined,
                color: getSpeakerColor(datoSpeakerId, 0),
              },
              mappedLabels: speakerLabels,
            });
          }
        }

        // Map ALL labels for this person to the same Dato speaker ID
        // This handles consolidation without needing segment reassignment
        for (const label of speakerLabels) {
          allMappings[label] = datoSpeakerId;
          // PLAT-9178: Write-through cache so labels resolve immediately
          cacheAndWriteSpeakerAssignment(
            storyId,
            label,
            mapping.name,
            // The label is mapped now, so its colour comes from the Dato id —
            // the rule useSpeakerLabels applies once a mapping exists.
            getSpeakerColor(datoSpeakerId, 0),
          );
        }
      }

      // Update all mappings at once
      if (Object.keys(allMappings).length > 0) {
        setApplyingProgress('Saving...');
        const success = await updateSpeakerVoiceMappings(storyId, {
          mappings: allMappings,
          storyId,
          lastUpdated: Date.now(),
        });
        if (!success) {
          throw new Error('Failed to update speaker mappings');
        }
      }

      // Optimistic panel update FIRST — must fire before notifySpeakerCacheUpdated
      // because that triggers a CDN re-fetch which would overwrite the optimistic state.
      for (const { speaker, mappedLabels } of addedSpeakers) {
        notifySpeakerAddedToStory(
          storyId,
          speaker,
          undefined,
          undefined,
          mappedLabels,
        );
      }
      // Now notify hooks and clear caches
      notifySpeakerCacheUpdated(storyId);
      clearSpeakerUtilsCache();
      onClose();
      // Pass all mapped labels so the panel can immediately remove them from anonymous
      const allMappedLabels = Object.keys(allMappings);
      onIdentificationComplete(allMappedLabels);
    } catch (err) {
      logger.error('Error applying speaker mappings:', err);
      setError('Failed to apply some speaker mappings. Please try again.');
      setIsApplying(false);
      setApplyingProgress('');
    }
  };

  const hasAnyMappings = Object.values(selectedMappings).some(
    mapping => mapping !== null,
  );

  // Every row carries the tile, or none does. A tile that appeared only for the
  // speakers whose frame resolved would shift the arrow and dropdown of every
  // other row 96px out of line — and a speaker whose frame is missing can still
  // have their moment played.
  const showMomentColumn = anonymousSpeakers.some(
    speaker =>
      speakerThumbnails[speaker.speakerLabel] ||
      (representativeTurns[speaker.speakerLabel] !== undefined &&
        (playbackSource.mp4Url || playbackSource.muxPlaybackId)),
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  // Analyzing out of sight: the host shows a pending toast meanwhile.
  if (isHiddenWhileIdentifying) return null;

  const isBusy = isIdentifying || isAnalyzingFrames;

  return (
    <ModalOverlay
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <ModalContent
        $size="xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
      >
        <CloseButton onClick={onClose} aria-label="Close modal">
          <CloseIcon />
        </CloseButton>

        <DialogHeader>
          <DialogTitle id={TITLE_ID}>Identify Speakers</DialogTitle>
          <DialogText>
            Name who&rsquo;s speaking so Arbor can add nametags automatically
            and find clips by speaker.
          </DialogText>
        </DialogHeader>

        <ModalBody>
          {!hasResult && !isBusy && (
            <IntroSection>
              <DialogText>
                Analyze the transcript to find who introduces themselves or is
                mentioned by name.
              </DialogText>
              <PrimaryButton type="button" onClick={handleIdentifySpeakers}>
                Analyze Transcript
              </PrimaryButton>
            </IntroSection>
          )}

          {isBusy && (
            <LoadingSection>
              <LoadingState
                size="lg"
                message={
                  identificationProgress ||
                  (isAnalyzingFrames
                    ? 'Analyzing video frames for speaker information...'
                    : 'Analyzing transcript for speaker identities...')
                }
              />
            </LoadingSection>
          )}

          {hasResult && !isBusy && (
            <>
              <ResultsHeader>{resultSummary}</ResultsHeader>

              <MappingsContainer>
                {anonymousSpeakers.map(speaker => {
                  const currentMapping = selectedMappings[speaker.speakerLabel];
                  const speakerColor =
                    getSpeakerColorFromLabel(speaker.speakerLabel) ||
                    'var(--text-muted)';
                  const speakerNumber =
                    parseInt(speaker.speakerLabel.replace('spk_', '')) + 1;
                  const thumbnailUrl = speakerThumbnails[speaker.speakerLabel];
                  const candidate = candidateFor(speaker.speakerLabel);

                  return (
                    <MappingRow key={speaker.speakerLabel}>
                      <AnonymousSpeakerCell>
                        <SpeakerColorIndicator color={speakerColor} />
                        <SpeakerLabel>Speaker {speakerNumber}</SpeakerLabel>
                      </AnonymousSpeakerCell>

                      {showMomentColumn && (
                        <SpeakerMomentCell
                          frameUrl={thumbnailUrl}
                          speakerName={`Speaker ${speakerNumber}`}
                          turn={representativeTurns[speaker.speakerLabel]}
                          mp4Url={playbackSource.mp4Url}
                          muxPlaybackId={playbackSource.muxPlaybackId}
                          isActive={
                            playingSpeakerLabel === speaker.speakerLabel
                          }
                          isPlaying={
                            playingSpeakerLabel === speaker.speakerLabel &&
                            isPreviewPlaying
                          }
                          onToggle={() => togglePreview(speaker.speakerLabel)}
                          onEnded={stopPreview}
                        />
                      )}

                      <Arrow aria-hidden="true">&rarr;</Arrow>

                      <MappingDropdown
                        currentMapping={currentMapping}
                        candidate={candidate}
                        existingSpeakers={existingSpeakers}
                        rowLabel={`Speaker ${speakerNumber}`}
                        onChange={mapping =>
                          handleMappingChange(speaker.speakerLabel, mapping)
                        }
                        onSaveDraft={(name, title) =>
                          commitName(speaker.speakerLabel, name, title)
                        }
                        disabled={isApplying}
                      />
                    </MappingRow>
                  );
                })}
              </MappingsContainer>
            </>
          )}

          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        </ModalBody>

        {hasResult && (
          <DialogFooter>
            <SecondaryButton type="button" onClick={onClose}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={handleApplyMappings}
              disabled={!hasAnyMappings || isApplying}
            >
              {isApplying
                ? applyingProgress || 'Applying...'
                : 'Apply Mappings'}
            </PrimaryButton>
          </DialogFooter>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

// ============================================================================
// MAPPING DROPDOWN SUB-COMPONENT
// ============================================================================

interface MappingDropdownProps {
  currentMapping: IdentifiedSpeaker | ExistingSpeaker | null;
  /** The name on offer as "New" for this row, if anything named them. */
  candidate?: IdentifiedSpeaker;
  existingSpeakers: ExistingSpeaker[];
  /** Names the edit fields for screen readers: "Speaker 2". */
  rowLabel: string;
  onChange: (mapping: IdentifiedSpeaker | ExistingSpeaker | null) => void;
  /** The name and title typed into the menu's edit form. */
  onSaveDraft: (name: string, title: string) => void;
  disabled?: boolean;
}

const MappingDropdown: React.FC<MappingDropdownProps> = ({
  currentMapping,
  candidate,
  existingSpeakers,
  rowLabel,
  onChange,
  onSaveDraft,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // The menu shows the edit form instead of the list while this is set.
  const [draft, setDraft] = useState<{
    heading: string;
    name: string;
    title: string;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const close = () => {
    setIsOpen(false);
    setDraft(null);
  };
  // Portaled below the trigger, dismissed on click-outside / scroll / Escape
  // (docs/design-system.md → Dropdown / Popover Menus).
  const { menuRef, style: menuStyle } = useAnchoredDropdown(
    isOpen,
    buttonRef,
    close,
    280,
  );

  const handleSelect = (
    mapping: IdentifiedSpeaker | ExistingSpeaker | null,
  ) => {
    onChange(mapping);
    close();
  };

  // Suggestions come from the same scorer the modal matches with, at the review
  // threshold — ranking is what a suggestion list wants, so best-first order
  // falls out of it. A hand-rolled filter here disagreed with the matcher in
  // both directions: it promoted namesakes sharing only a surname and hid
  // diacritic variants the scorer rates a perfect match.
  const potentialMatches = candidate
    ? rankSpeakerMatches(
        candidate,
        existingSpeakers,
        MATCH_THRESHOLD_REVIEW,
      ).map(({ match }) => match)
    : [];

  const isExisting = !!currentMapping && 'datoSpeakerId' in currentMapping;
  const isNew = !!currentMapping && !isExisting;
  const triggerTitle =
    currentMapping && 'title' in currentMapping ? currentMapping.title : '';
  const triggerText = currentMapping
    ? `${currentMapping.name}${triggerTitle ? ` | ${triggerTitle}` : ''}`
    : 'Not identified';
  const triggerColor = !currentMapping
    ? ''
    : 'datoSpeakerId' in currentMapping
      ? currentMapping.color
      : getSpeakerColorFromLabel(currentMapping.speakerLabel);

  // The full name and title on hover — only when the field cut one of them
  // off, since an uncut field already says everything the tooltip would.
  const nameRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const measureTruncation = () =>
    setIsTruncated(
      [nameRef.current, titleRef.current].some(
        el => !!el && el.scrollWidth > el.clientWidth,
      ),
    );
  const tooltipId = `speaker-mapping-${rowLabel.replace(/\s+/g, '-')}`;

  const isExistingSelected = (speaker: ExistingSpeaker) =>
    isExisting && currentMapping.datoSpeakerId === speaker.datoSpeakerId;

  const renderSpeakerItem = (
    key: string,
    speaker: { name: string; title?: string; color: string },
    isSelected: boolean,
    onClick: () => void,
    badge?: React.ReactNode,
  ) => (
    <DropdownItem
      key={key}
      onClick={onClick}
      $isSelected={isSelected}
      type="button"
    >
      <SpeakerAvatar
        name={speaker.name || '?'}
        color={speaker.color}
        size={28}
      />
      <ItemInfo>
        <ItemName>{speaker.name}</ItemName>
        {speaker.title && <ItemTitle>{speaker.title}</ItemTitle>}
      </ItemInfo>
      {badge}
    </DropdownItem>
  );

  const dropdownMenu =
    isOpen && menuStyle ? (
      <DropdownMenu
        ref={menuRef}
        style={menuStyle}
        data-testid="speaker-mapping-menu"
      >
        {draft ? (
          <SpeakerDraftForm
            heading={draft.heading}
            initialName={draft.name}
            initialTitle={draft.title}
            rowLabel={rowLabel}
            onSave={(name, title) => {
              onSaveDraft(name, title);
              close();
            }}
            onCancel={() => setDraft(null)}
          />
        ) : (
          <>
            <DropdownItem
              type="button"
              $isSelected={currentMapping === null}
              $muted
              onClick={() => handleSelect(null)}
            >
              Leave Unnamed
            </DropdownItem>

            {candidate && (
              // The speaker Apply would create, and the pencil to describe
              // them first. Side by side, since a button cannot hold another.
              <NewSpeakerRow>
                {renderSpeakerItem(
                  'create-new',
                  {
                    name: candidate.name,
                    title: candidate.title,
                    color: getSpeakerColorFromLabel(candidate.speakerLabel),
                  },
                  isNew,
                  () => handleSelect(candidate),
                  <ItemBadge $tone="new">New</ItemBadge>,
                )}
                <EditSpeakerButton
                  type="button"
                  aria-label={`Edit ${candidate.name}`}
                  onClick={() =>
                    setDraft({
                      heading: 'Edit Speaker',
                      name: candidate.name,
                      title: candidate.title,
                    })
                  }
                >
                  <PencilIcon
                    width="14"
                    height="14"
                    strokeColor="currentColor"
                  />
                </EditSpeakerButton>
              </NewSpeakerRow>
            )}

            {potentialMatches.length > 0 && (
              <>
                <DropdownSectionHeader>Suggested Matches</DropdownSectionHeader>
                {potentialMatches.map(speaker =>
                  renderSpeakerItem(
                    speaker.datoSpeakerId,
                    speaker,
                    isExistingSelected(speaker),
                    () => handleSelect(speaker),
                  ),
                )}
              </>
            )}

            {existingSpeakers.length > potentialMatches.length && (
              <>
                <DropdownSectionHeader>
                  All Existing Speakers
                </DropdownSectionHeader>
                {existingSpeakers
                  .filter(speaker => !potentialMatches.includes(speaker))
                  .map(speaker =>
                    renderSpeakerItem(
                      speaker.datoSpeakerId,
                      speaker,
                      isExistingSelected(speaker),
                      () => handleSelect(speaker),
                    ),
                  )}
              </>
            )}

            {/* The way out when nothing above is right. Pinned, so a long
            library does not bury it. */}
            <ActionItems>
              <DropdownItem
                type="button"
                onClick={() =>
                  // Someone else entirely: the title read for this row is not theirs.
                  setDraft({ heading: 'New Speaker', name: '', title: '' })
                }
              >
                <ActionIcon>
                  <PlusIcon
                    width="14"
                    height="14"
                    strokeColor="currentColor"
                    strokeWidth={4}
                  />
                </ActionIcon>
                Create New Speaker…
              </DropdownItem>
            </ActionItems>
          </>
        )}
      </DropdownMenu>
    ) : null;

  return (
    <DropdownContainer>
      <DropdownButton
        ref={buttonRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={triggerText}
        disabled={disabled}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        onMouseEnter={measureTruncation}
        data-tooltip-id={isTruncated && !isOpen ? tooltipId : undefined}
        data-tooltip-content={triggerText}
      >
        {currentMapping ? (
          <>
            <SpeakerAvatar
              name={currentMapping.name || '?'}
              color={triggerColor}
              size={28}
            />
            <ItemInfo>
              <TriggerName ref={nameRef}>{currentMapping.name}</TriggerName>
              {triggerTitle && (
                <ItemTitle ref={titleRef}>{triggerTitle}</ItemTitle>
              )}
            </ItemInfo>
          </>
        ) : (
          <DropdownText $muted>{triggerText}</DropdownText>
        )}
        {isNew && <ItemBadge $tone="new">New</ItemBadge>}
        {isExisting && <ItemBadge $tone="existing">Existing</ItemBadge>}
        <DropdownChevron $isOpen={isOpen}>
          <ChevronDownIcon />
        </DropdownChevron>
      </DropdownButton>

      {dropdownMenu && ReactDOM.createPortal(dropdownMenu, document.body)}
      {ReactDOM.createPortal(
        <Tooltip
          id={tooltipId}
          style={{ ...MODAL_TOOLTIP_STYLE, maxWidth: '420px' }}
          place="top"
          positionStrategy="fixed"
        />,
        document.body,
      )}
    </DropdownContainer>
  );
};

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

// Its own overlay rather than the shared `Modal`: the modal stays unmounted
// while the background identification runs, and `useOverlayDismiss` keeps a
// drag that overshoots the card from closing it (the SpeakerFormModal idiom).
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: var(--modal-backdrop);
  backdrop-filter: var(--modal-blur);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(${TOP_LAYER_VARS.modalBackdrop}, 1000006);
`;

// The shared dialog card (docs/design-system.md → Modals & Dialogs). `xl`
// because each row is a frame, a label and a picker side by side.
const ModalContent = styled(DialogCard)`
  position: relative;
  max-height: calc(100vh - 2 * var(--spacing-lg));
  min-height: 0;
`;

// Standard close button (docs/design-system.md → Close Buttons), placed where
// the shared Modal puts its own.
const CloseButton = styled.button`
  position: absolute;
  top: var(--spacing-sm);
  right: var(--spacing-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm);
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  min-height: 0;
  overflow-y: auto;
`;

const IntroSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-lg);
  padding: var(--spacing-2xl) var(--spacing-lg);
  text-align: center;

  ${DialogText} {
    max-width: 400px;
  }
`;

const LoadingSection = styled.div`
  padding: var(--spacing-3xl) var(--spacing-lg);
`;

const ResultsHeader = styled(DialogText)`
  color: var(--text-muted);
`;

const MappingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const MappingRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--surface-subtle);
  border-radius: var(--radius);
`;

const AnonymousSpeakerCell = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 0 0 100px;
`;

const SpeakerColorIndicator = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

const SpeakerLabel = styled.span`
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
`;

const Arrow = styled.span`
  color: var(--text-muted);
  font-size: var(--text-lg);
`;

const DropdownContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

// A closed select: the bordered-field pattern (docs/design-system.md → Form
// Fields), so it hovers and focuses like every other field.
const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  min-height: 52px;
  padding: var(--spacing-xs) var(--spacing-md);
  background-color: var(--input-background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  text-align: left;
  cursor: pointer;
  transition: border-color var(--transition-fast);

  &:hover:not(:disabled) {
    border-color: var(--text-muted);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const NewSpeakerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  & > button:first-child {
    flex: 1;
    min-width: 0;
  }
`;

// A ghost icon button beside the New item it edits.
const EditSpeakerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  background: none;
  border: none;
  border-radius: 6px;
  color: var(--text-muted);
  cursor: pointer;
  transition:
    color var(--transition-fast),
    background-color var(--transition-fast);

  &:hover:not(:disabled) {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const DropdownText = styled.span<{ $muted?: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${props => (props.$muted ? 'var(--text-muted)' : 'inherit')};
`;

const ItemBadge = styled.span<{ $tone: 'new' | 'existing' }>`
  flex-shrink: 0;
  padding: var(--spacing-2xs) var(--spacing-xs);
  border-radius: var(--radius-input);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  background-color: ${props =>
    props.$tone === 'new' ? 'var(--primary-tint)' : 'var(--accent-tint)'};
  color: ${props =>
    props.$tone === 'new' ? 'var(--primary)' : 'var(--accent)'};
  border: 1px solid
    ${props =>
      props.$tone === 'new'
        ? 'var(--primary-tint-strong)'
        : 'var(--accent-tint-strong)'};
`;

const DropdownChevron = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--text-muted);
  transition: transform 0.2s ease;
  transform: ${props => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

// Rounded rows on container padding, no divider lines (docs/design-system.md
// → Dropdown / Popover Menus → Menu-item styling).
const DropdownMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--spacing-xs);
  background-color: var(--popover);
  border: 1px solid var(--border-hairline);
  border-radius: var(--radius);
  box-shadow: var(--elevation-md);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--border);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const DropdownItem = styled.button<{ $isSelected?: boolean; $muted?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  min-height: 36px;
  padding: var(--spacing-xs) 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: ${props => {
    if (props.$isSelected) return 'var(--accent)';
    return props.$muted ? 'var(--text-muted)' : 'var(--text-secondary)';
  }};
  text-align: left;
  cursor: pointer;
  transition:
    background-color var(--transition-fast) ease,
    color var(--transition-fast) ease;

  &:hover {
    background-color: var(--surface-elevated);
    color: ${props =>
      props.$isSelected ? 'var(--accent)' : 'var(--foreground)'};
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The picked speaker's name in the closed field: full-strength, unlike a menu
// item's, which takes the item's muted/selected colour.
const TriggerName = styled(ItemName)`
  color: var(--foreground);
`;

const ItemTitle = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownSectionHeader = styled.div`
  padding: var(--spacing-sm) 10px var(--spacing-xs);
  margin-top: var(--spacing-xs);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-muted);
  pointer-events: none;

  &:first-child {
    margin-top: 0;
  }
`;

const ActionItems = styled.div`
  position: sticky;
  bottom: calc(-1 * var(--spacing-xs));
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: var(--spacing-xs);
  padding: var(--spacing-xs) 0;
  border-top: 1px solid var(--border-hairline);
  background-color: var(--popover);
`;

const ActionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  flex-shrink: 0;
`;

const ErrorMessage = styled.div`
  color: var(--destructive);
  font-size: var(--text-sm);
`;

export default CreatorStudioSpeakerIdentificationModal;
